import {JobRead} from "../schemas";
import {apiFetch} from "./client";
import {uploadFilesInParallel} from "./gcs-upload-helper";
import {computeDeclaredPages} from "./page-count";

/**
 * Accepted file extensions for upload — source of truth for the frontend.
 * Maps extension → file type category. Mirrors UPLOAD_ACCEPTED_EXTENSIONS
 * in backend upload_api.py.
 *
 * PTX files are court transcript exports (RealLegal/LexisNexis). They can
 * be renamed PDFs, renamed ZIPs, or plain text — the backend detects the
 * actual content from magic bytes and routes accordingly.
 */
export const UPLOAD_ACCEPTED_EXTENSIONS: Record<string, string> = {
    pdf: "PDF",
    txt: "TXT",
    ptx: "TXT",   // court transcript exports — actual format detected by backend
    zip: "ZIP",
    // ── Not yet enabled ──
    // gz: "GZ",
    // tar: "TAR",
    // eml: "EML",
    // msg: "MSG",
    // mbox: "MBOX",
    // pst: "PST",
};

/** File extensions accepted for upload (for HTML input accept attributes). */
export const UPLOAD_ACCEPT_STRING = Object.keys(UPLOAD_ACCEPTED_EXTENSIONS)
    .map(ext => `.${ext}`)
    .join(",");

/** MIME types for accepted upload extensions (for Uppy restrictions). */
export const UPLOAD_ACCEPTED_MIME_TYPES: Record<string, string[]> = {
    pdf: ["application/pdf"],
    txt: ["text/plain"],
    ptx: ["text/plain", "application/pdf", "application/zip", "application/octet-stream"],
    zip: ["application/zip", "application/x-zip-compressed", "application/octet-stream"],
};

/** Per-type file size limits: warn threshold and hard reject threshold.
 *  warn = show warning but allow; max = reject outright. */
export const UPLOAD_SIZE_LIMITS: Record<string, { warn: number; max: number }> = {
    PDF: { warn: 30 * 1024 * 1024, max: 50 * 1024 * 1024 },   // 30MB warn, 50MB reject
    TXT: { warn: 20 * 1024 * 1024, max: 30 * 1024 * 1024 },   // 20MB warn, 30MB reject
    ZIP: { warn: 50 * 1024 * 1024, max: 100 * 1024 * 1024 },   // 50MB warn, 100MB reject
};

/**
 * Detect the actual file type of a PTX file by reading magic bytes.
 * PTX files can be renamed PDFs, renamed ZIPs, or plain text transcripts.
 */
export async function detectPtxType(file: File): Promise<"PDF" | "ZIP" | "TXT" | "UNSUPPORTED"> {
    const head = await file.slice(0, 64).arrayBuffer();
    const bytes = new Uint8Array(head);

    // %PDF-
    if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
        return "PDF";
    }
    // PK\x03\x04 (ZIP)
    if (bytes[0] === 0x50 && bytes[1] === 0x4B && bytes[2] === 0x03 && bytes[3] === 0x04) {
        return "ZIP";
    }
    // Check if mostly printable text (not encrypted binary)
    const check = Math.min(bytes.length, 64);
    let nonPrintable = 0;
    for (let i = 0; i < check; i++) {
        if (bytes[i] < 32 && bytes[i] !== 10 && bytes[i] !== 13 && bytes[i] !== 9) {
            nonPrintable++;
        }
    }
    if (nonPrintable > check * 0.1) {
        return "UNSUPPORTED"; // likely encrypted RealLegal format
    }
    return "TXT";
}

/**
 * Validate a file for upload. Returns error/warning messages and detected type.
 */
export async function validateUploadFile(file: File): Promise<{
    error: string | null;
    warning: string | null;
    detectedType?: string;
}> {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";

    if (!(ext in UPLOAD_ACCEPTED_EXTENSIONS)) {
        return {error: `".${ext}" is not a supported file type.`, warning: null};
    }

    // PTX: detect actual format and give immediate feedback
    let detectedType: string | undefined;
    if (ext === "ptx") {
        detectedType = await detectPtxType(file);
        if (detectedType === "UNSUPPORTED") {
            return {
                error: `${file.name} appears to be an encrypted RealLegal PTX file. Export it as PDF from RealLegal E-Transcript Viewer before uploading.`,
                warning: null,
                detectedType,
            };
        }
    }

    // Resolve the effective type category for size checks
    // PTX uses its detected type; otherwise use the extension mapping
    const typeCategory = detectedType ?? UPLOAD_ACCEPTED_EXTENSIONS[ext] ?? "PDF";
    const limits = UPLOAD_SIZE_LIMITS[typeCategory];

    if (limits) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(0);
        const maxMB = (limits.max / 1024 / 1024).toFixed(0);
        if (file.size > limits.max) {
            return {
                error: `${file.name} is ${sizeMB}MB — maximum ${typeCategory} size is ${maxMB}MB.`,
                warning: null,
                detectedType,
            };
        }
        if (file.size > limits.warn) {
            return {
                error: null,
                warning: `${file.name} is ${sizeMB}MB — large files may take longer to process and could fail.`,
                detectedType,
            };
        }
    }

    return {error: null, warning: null, detectedType};
}

export async function queueUploadBatch(
    folders: { folderName: string; files: File[] }[],
    groupId: string,
    summarizationWordLimit: number,
    investigationContext?: string,
): Promise<JobRead[]> {
    const clientKey = (f: File) => `${f.name}::${f.size}::${f.lastModified}`;

    interface SignedUpload {
        client_key: string;
        session_url: string;
        file_name: string;
        doc_id: string;
        folder_id: string;
    }

    interface QueueUploadFilesResponse {
        jobs: JobRead[];
        signed_uploads: SignedUpload[];
    }

    // Best-effort per-file page counts (PDFs exact, text by char convention).
    // Lets the server reject an over-quota batch up front; null falls back to a
    // server-side estimate. Keyed by client_key to align with each file.
    const declaredPagesByKey = new Map<string, number | null>(
        await Promise.all(
            folders.flatMap((folder) => folder.files).map(
                async (f): Promise<[string, number | null]> => [
                    clientKey(f),
                    await computeDeclaredPages(f),
                ],
            ),
        ),
    );

    const payload = {
        folders: folders.map((folder) => ({
            folder_name: folder.folderName,
            files: folder.files.map((f) => ({
                client_key: clientKey(f),
                file_name: f.name,
                content_type: f.type || "application/octet-stream",
                file_size_in_bytes: f.size,
                declared_page_count: declaredPagesByKey.get(clientKey(f)) ?? null,
            })),
        })),
        group_id: groupId,
        summarization_word_limit: summarizationWordLimit,
        investigation_context: investigationContext,
    };

    const data = await apiFetch<QueueUploadFilesResponse>(
        "/uploads/queue_upload_batch",
        {method: "POST", body: payload},
    );

    const {jobs, signed_uploads} = data;

    const allFiles: File[] = folders.flatMap((folder) => folder.files);
    const signedUploadsByKey = new Map(
        signed_uploads.map((su) => [su.client_key, su]),
    );

    if (signed_uploads.length !== allFiles.length) {
        throw new Error(
            `Signed uploads length mismatch: expected ${allFiles.length}, got ${signed_uploads.length}`,
        );
    }

    const uploadsToProcess = allFiles.map((f) => {
        const key = clientKey(f);
        const su = signedUploadsByKey.get(key);
        if (!su) throw new Error(`No session URL for ${f.name} (${key})`);
        return {
            sessionUrl: su.session_url,
            file: f,
            docId: su.doc_id,
            contentType: f.type || undefined,
        };
    });

    const {succeeded, failed} = await uploadFilesInParallel(uploadsToProcess, {
        concurrency: 8,
        onFileComplete: (docId) => console.log(`[batch-upload] File completed: ${docId}`),
        onFileError: (docId, error) => console.error(`[batch-upload] File failed: ${docId}`, error),
    });

    if (failed.length > 0) {
        console.error(`[batch-upload] ${failed.length} file(s) failed to upload:`, failed);
    }

    console.log(
        `[batch-upload] Completed: ${succeeded.length}/${allFiles.length} files across ${folders.length} folders`,
    );
    return jobs;
}
